import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { notificationsAPI } from '../services/api';
import NotificationDropdown from './NotificationDropdown';
import './Navbar.css';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchCurrentX = useRef<number>(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'fr' : 'en');
  };

  // Handle swipe to close menu
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!menuOpen) return;
    touchStartX.current = e.touches[0].clientX;
    setIsSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!menuOpen || !isSwiping) return;
    touchCurrentX.current = e.touches[0].clientX;

    // If swiping from left to right (positive delta)
    const deltaX = touchCurrentX.current - touchStartX.current;
    if (deltaX > 0 && menuRef.current) {
      // Apply transform to follow finger
      menuRef.current.style.transform = `translateX(${deltaX}px)`;
    }
  };

  const handleTouchEnd = () => {
    if (!menuOpen || !isSwiping) return;

    const deltaX = touchCurrentX.current - touchStartX.current;

    // If swipe distance is more than 80px, close the menu
    if (deltaX > 80) {
      setMenuOpen(false);
    }

    // Reset transform
    if (menuRef.current) {
      menuRef.current.style.transform = '';
    }

    setIsSwiping(false);
    touchStartX.current = 0;
    touchCurrentX.current = 0;
  };

  const isActive = (path: string) => location.pathname === path;

  const getPhotoUrl = (photoPath?: string) => {
    if (!photoPath) return null;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    return `${baseUrl.replace(/\/api$/, '')}${photoPath}`;
  };

  const getInitials = () => {
    if (!user) return '';
    return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
  };

  const fetchUnreadCount = async () => {
    try {
      const data = await notificationsAPI.getUnreadCount();
      setUnreadCount(data.count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Refresh unread count every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  return (
    <nav className="navbar">
      <div className="nav-container">
        <Link to={user ? "/map" : "/login"} className="nav-brand">
          <svg viewBox="0 0 60 32" xmlns="http://www.w3.org/2000/svg" className="nav-brand-logo">
            <circle cx="16" cy="16" r="16" fill="#00745e"/>
            <text x="16" y="20" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="700" fill="white">bob</text>
            <path d="M13.5 19 L16 24 L18.5 19" fill="white"/>
            <text x="46" y="20" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="700" fill="#00745e">map</text>
          </svg>
        </Link>

        {user && (
          <>
            {/* Notification button - visible on both desktop and mobile */}
            <button
              className="notification-button notification-button-mobile"
              onClick={() => {
                setNotificationsOpen(!notificationsOpen);
                setMenuOpen(false); // Close hamburger menu when opening notifications
              }}
              title="Notifications"
            >
              🔔
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </button>

            <NotificationDropdown
              isOpen={notificationsOpen}
              onClose={() => setNotificationsOpen(false)}
              onUnreadCountChange={setUnreadCount}
            />

            <button className="nav-toggle" onClick={() => setMenuOpen(!menuOpen)}>
              <span></span>
              <span></span>
              <span></span>
            </button>

            {/* Desktop Navigation Links */}
            <div className="nav-links-desktop">
              <Link
                to="/map"
                className={`nav-link ${isActive('/map') ? 'active' : ''}`}
              >
                {t('nav.map')}
              </Link>
              <Link
                to="/friends"
                className={`nav-link ${isActive('/friends') ? 'active' : ''}`}
              >
                {t('nav.friends')}
              </Link>
            </div>

            {/* Overlay when menu is open */}
            {menuOpen && (
              <div
                className="nav-overlay"
                onClick={() => setMenuOpen(false)}
              />
            )}

            {/* Mobile Menu (hamburger) */}
            <div
              ref={menuRef}
              className={`nav-menu ${menuOpen ? 'active' : ''}`}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Navigation Links - Mobile */}
              <Link
                to="/map"
                className={`nav-link ${isActive('/map') ? 'active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                {t('nav.map')}
              </Link>
              <Link
                to="/friends"
                className={`nav-link ${isActive('/friends') ? 'active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                {t('nav.friends')}
              </Link>
              <Link
                to="/profile"
                className={`nav-link nav-link-mobile-only ${isActive('/profile') ? 'active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                {t('nav.myLocations')}
              </Link>
              <Link
                to="/settings"
                className={`nav-link nav-link-mobile-only ${isActive('/settings') ? 'active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                {t('nav.settings')}
              </Link>

              {/* Logout Button - Mobile */}
              <button
                onClick={() => {
                  handleLogout();
                  setMenuOpen(false);
                }}
                className="btn-logout btn-logout-mobile"
              >
                {t('nav.logout')}
              </button>

              {/* Language switcher - Mobile */}
              <button
                className="language-switcher language-switcher-mobile-menu"
                onClick={toggleLanguage}
                title={language === 'en' ? t('nav.languageSwitchToFrench') : t('nav.languageSwitchToEnglish')}
              >
                {language === 'en' ? '🇫🇷' : '🇬🇧'}
              </button>

              {/* User profile section at bottom - Mobile only */}
              <Link
                to="/profile"
                className="nav-user-profile-mobile"
                onClick={() => setMenuOpen(false)}
              >
                <div className="user-avatar-mobile">
                  {user.profile_photo && getPhotoUrl(user.profile_photo) ? (
                    <img
                      src={getPhotoUrl(user.profile_photo) || undefined}
                      alt={`${user.first_name} ${user.last_name}`}
                      className="avatar-img-mobile"
                    />
                  ) : (
                    <div className="avatar-initials-mobile">{getInitials()}</div>
                  )}
                </div>
                <div className="user-name-mobile">
                  {user.first_name} {user.last_name}
                </div>
              </Link>
            </div>

            {/* Desktop Right Section: Nom, Langues, Logout, Settings */}
            <div
              className="user-name-desktop"
              onClick={() => {
                // On desktop, open profile panel on map
                if (window.innerWidth > 768) {
                  // If already on map, signal to open panel via custom event
                  if (location.pathname === '/map') {
                    window.dispatchEvent(new CustomEvent('openOwnProfilePanel'));
                  } else {
                    // Navigate to map and signal to open panel
                    navigate('/map', { state: { openOwnProfile: true } });
                  }
                } else {
                  // On mobile, navigate to profile page (fallback, shouldn't happen)
                  navigate('/profile');
                }
              }}
            >
              {user.first_name} {user.last_name}
            </div>

            <button
              className="language-switcher"
              onClick={toggleLanguage}
              title={language === 'en' ? t('nav.languageSwitchToFrench') : t('nav.languageSwitchToEnglish')}
            >
              {language === 'en' ? '🇫🇷' : '🇬🇧'}
            </button>

            <button onClick={handleLogout} className="btn-logout">
              {t('nav.logout')}
            </button>

            <Link
              to="/settings"
              className="settings-button"
              title={t('nav.settings')}
            >
              ⚙️
            </Link>
          </>
        )}

        {!user && (
          <button
            className="language-switcher language-switcher-alone"
            onClick={toggleLanguage}
            title={language === 'en' ? t('nav.languageSwitchToFrench') : t('nav.languageSwitchToEnglish')}
          >
            {language === 'en' ? '🇫🇷' : '🇬🇧'}
          </button>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
